import { fetchWithAuth } from '@/utils/api-error-handler'

// Mapping of metric names to task IDs
const METRIC_TO_TASK_ID: Record<string, string> = {
  fold_run_sandbox: 'a0000001-0001-4000-8000-000000000001',
  job_share: 'a0000001-0001-4000-8000-000000000002',
  api_key_create: 'a0000001-0001-4000-8000-000000000003',
  agent_skill_install: 'a0000001-0001-4000-8000-000000000004',
  agent_cli_visit: 'a0000001-0001-4000-8000-000000000005',
}

// Client-side cache of completed task IDs – no API call if task is already complete
const completedTaskIds = new Set<string>()

/**
 * Updates the cache of completed onboarding task IDs.
 * Call this when you have fresh onboarding status (e.g. after fetching /v1/user/onboarding-status).
 * Used so we skip POST /complete when a task is already complete.
 */
export function setOnboardingCompletedTaskIds(taskIds: string[]): void {
  completedTaskIds.clear()
  taskIds.forEach((id) => completedTaskIds.add(id))
}

/**
 * Marks an onboarding task as complete based on the metric name.
 * Does nothing if the task is already complete (no API call, no event).
 * Fails silently on error. Dispatches a custom event only when a task is newly completed.
 *
 * @param metricName - The Sentry metric name that corresponds to a task
 */
export async function markOnboardingTaskComplete(metricName: string): Promise<void> {
  try {
    const taskId = METRIC_TO_TASK_ID[metricName]
    if (!taskId) return

    // Skip any call if this task is already complete
    if (completedTaskIds.has(taskId)) return

    const API_URL = process.env.NEXT_PUBLIC_API_URL?.replace(/\/$/, '')
    if (!API_URL) return

    const response = await fetchWithAuth(
      `${API_URL}/v1/user/onboarding-task/${taskId}/complete`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' }
      },
      undefined,
      { skipUnauthorizedRedirect: true }
    )

    if (response.ok) {
      completedTaskIds.add(taskId)
      const event = new CustomEvent('onboarding-task-completed', {
        detail: { metricName, taskId }
      })
      window.dispatchEvent(event)
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.debug('[Onboarding] Failed to mark task complete:', metricName, error)
    }
  }
}
